// components/header/DeliveryAddressDisplay.tsx
// TopBar의 "Deliver to ..." 영역 표시 — 클릭 시 ZipInputModal 열림
// 두 가지 variant:
//   - 'stacked' (default, 데스크톱): 두 줄로 표시 ("Deliver to" + "neighborhood ZIP")
//   - 'inline' (모바일): 한 줄로 압축 표시 ("📍 Deliver to neighborhood 10018 ▼")
//
// 상태별 색상:
//   - 미설정: golden CTA
//   - In-zone: white
//   - Partial: amber
//   - Out of zone: rose

'use client';

import { useState } from 'react';
import { useIsClient } from '@/hooks/useClientSnapshot';
import { FiMapPin, FiAlertCircle, FiChevronDown } from 'react-icons/fi';
import { useDeliveryAddress } from '@/context/DeliveryAddressContext';
import { getShortAddressLabel } from '@/lib/delivery/geofence';
import { ZipInputModal } from './ZipInputModal';

interface DeliveryAddressDisplayProps {
  /**
   * stacked = 데스크톱 (2줄, "Deliver to / Chelsea 10011")
   * inline  = 풀너비 pill (1줄, "Deliver to Chelsea 10011 ▼")
   * compact = 아주 작은 pill (icon + ZIP 또는 'Set ZIP') — 첫 줄 sign-in 옆 배치용
   */
  variant?: 'stacked' | 'inline' | 'compact';
}

interface DisplayContent {
  topLine: string;
  bottomLine: string;
  /** inline variant에서 한 줄로 합칠 때 사용 */
  inlineLabel: string;
  /** compact variant 용 — 아주 짧은 라벨 (ZIP 단독 또는 'Set ZIP') */
  compactLabel: string;
  /** 색상 (Tailwind class) */
  accentColor: string;
  iconColor: string;
  IconComponent: typeof FiMapPin | typeof FiAlertCircle;
  isCta: boolean;
}

export function DeliveryAddressDisplay({ variant = 'stacked' }: DeliveryAddressDisplayProps) {
  const { zone, address, geofence, isResolvingAddress, isRestored } = useDeliveryAddress();
  const [isModalOpen, setIsModalOpen] = useState(false);
  // SSR/CSR mismatch 방지 — 이 컴포넌트의 하이드레이션이 끝나고 localStorage 복원도 끝난 뒤에만 실제 값 표시
  // (헤더가 provider보다 늦게 하이드레이션되면 isRestored가 이미 true일 수 있다)
  const isClient = useIsClient();
  const mounted = isClient && isRestored;

  const display: DisplayContent = (() => {
    if (!mounted) {
      // Hydration 안정화 전 — TopBar 깜빡임 방지용 일관된 placeholder
      return {
        topLine: 'Deliver to',
        bottomLine: 'Manhattan, NY',
        inlineLabel: 'Deliver to Manhattan, NY',
        compactLabel: 'NY',
        accentColor: 'text-white',
        iconColor: 'text-white',
        IconComponent: FiMapPin,
        isCta: false,
      };
    }

    if (isResolvingAddress) {
      return {
        topLine: 'Delivery',
        bottomLine: 'Checking address',
        inlineLabel: 'Checking delivery address',
        compactLabel: 'Checking',
        accentColor: 'text-white',
        iconColor: 'text-white',
        IconComponent: FiMapPin,
        isCta: false,
      };
    }

    if (address && geofence) {
      const shortAddress = getShortAddressLabel(address);

      if (geofence.status === 'standard_available') {
        return {
          topLine: 'Deliver to',
          bottomLine: shortAddress || `${address.zip}`,
          inlineLabel: `Deliver to ${shortAddress || address.zip}`,
          compactLabel: address.zip,
          accentColor: 'text-white',
          iconColor: 'text-white',
          IconComponent: FiMapPin,
          isCta: false,
        };
      }

      if (geofence.status === 'extended_review_required' || geofence.status === 'blocked') {
        return {
          topLine: 'Review address',
          bottomLine: shortAddress || `${address.zip} — confirm`,
          inlineLabel: `${shortAddress || address.zip} — review delivery`,
          compactLabel: address.zip,
          accentColor: 'text-amber-300',
          iconColor: 'text-amber-300',
          IconComponent: FiAlertCircle,
          isCta: false,
        };
      }
    }

    if (!zone) {
      return {
        topLine: 'Delivery',
        bottomLine: 'Enter address',
        inlineLabel: 'Enter delivery address',
        compactLabel: 'Address',
        accentColor: 'text-[#febd69]',
        iconColor: 'text-[#febd69]',
        IconComponent: FiMapPin,
        isCta: true,
      };
    }

    switch (zone.status) {
      case 'in_zone': {
        const place = zone.neighborhood?.shortName ?? zone.neighborhood?.name ?? '';
        return {
          topLine: 'Deliver to',
          bottomLine: `${place} ${zone.zip}`,
          inlineLabel: `Deliver to ${place} ${zone.zip}`,
          compactLabel: zone.zip,
          accentColor: 'text-white',
          iconColor: 'text-white',
          IconComponent: FiMapPin,
          isCta: false,
        };
      }
      case 'partial':
        return {
          topLine: 'Maybe deliver to',
          bottomLine: `${zone.zip} — verify`,
          inlineLabel: `${zone.zip} — verify at checkout`,
          compactLabel: zone.zip,
          accentColor: 'text-amber-300',
          iconColor: 'text-amber-300',
          IconComponent: FiMapPin,
          isCta: false,
        };
      case 'out_of_zone':
        return {
          topLine: 'Request delivery',
          bottomLine: `${zone.zip} — call to confirm`,
          inlineLabel: `${zone.zip} — call to request delivery`,
          compactLabel: zone.zip,
          accentColor: 'text-rose-300',
          iconColor: 'text-rose-300',
          IconComponent: FiAlertCircle,
          isCta: false,
        };
      case 'invalid':
      default:
        return {
          topLine: 'Delivery',
          bottomLine: 'Enter address',
          inlineLabel: 'Enter delivery address',
          compactLabel: 'Address',
          accentColor: 'text-[#febd69]',
          iconColor: 'text-[#febd69]',
          IconComponent: FiMapPin,
          isCta: true,
        };
    }
  })();

  const Icon = display.IconComponent;

  // ── Stacked (데스크톱): 두 줄 ──
  if (variant === 'stacked') {
    return (
      <>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="flex items-start leading-tight hover:opacity-80 transition-opacity focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#febd69] focus-visible:ring-offset-2 focus-visible:ring-offset-[#131921] rounded px-1 -mx-1"
          aria-label="Set delivery address"
        >
          <Icon className={`mt-1 mr-1 ${display.iconColor}`} />
          <div className="text-xs text-left">
            <div className="text-gray-300">{display.topLine}</div>
            <div className={`font-bold ${display.accentColor} ${display.isCta ? 'underline decoration-dotted underline-offset-2' : ''}`}>
              {display.bottomLine}
            </div>
          </div>
        </button>

        <ZipInputModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </>
    );
  }

  // ── Compact (모바일 첫 줄, sign-in 옆): 작은 pill, icon + ZIP만 ──
  if (variant === 'compact') {
    return (
      <>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors min-h-[36px] flex-shrink-0 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#febd69] focus-visible:ring-offset-2 focus-visible:ring-offset-[#131921] ${
            display.isCta
              ? 'bg-[#febd69]/15 hover:bg-[#febd69]/25 border border-[#febd69]/40'
              : 'bg-white/5 hover:bg-white/10 border border-white/10'
          }`}
          aria-label={mounted && zone ? `Delivery ZIP: ${zone.zip}. Tap to change` : 'Set delivery address'}
        >
          <Icon className={`w-3.5 h-3.5 shrink-0 ${display.iconColor}`} />
          <span className={`tabular-nums ${display.accentColor}`}>
            {display.compactLabel}
          </span>
          <FiChevronDown className={`w-3 h-3 shrink-0 opacity-60 ${display.accentColor}`} />
        </button>

        <ZipInputModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </>
    );
  }

  // ── Inline (full-width pill, 검색바 위 등): 한 줄, 풀너비 ──
  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors min-h-[36px] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#febd69] focus-visible:ring-offset-2 focus-visible:ring-offset-[#131921] ${
          display.isCta
            ? 'bg-[#febd69]/15 hover:bg-[#febd69]/25 border border-[#febd69]/40'
            : 'bg-white/5 hover:bg-white/10 border border-white/10'
        }`}
        aria-label="Set delivery address"
      >
        <Icon className={`w-3.5 h-3.5 shrink-0 ${display.iconColor}`} />
        <span className={`truncate ${display.accentColor}`}>
          {display.inlineLabel}
        </span>
        <FiChevronDown className={`w-3 h-3 shrink-0 opacity-60 ${display.accentColor}`} />
      </button>

      <ZipInputModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}
